import { useState, useEffect, useCallback } from 'react';
import { GameProgress, GameSettings, GameStats } from '../types/game.d';
import { getGameProgressForGame, saveGameProgressForGame } from '../services/firebase';

const DEFAULT_SETTINGS: GameSettings = {
  difficulty: 'easy',
  interval: 5000,
  patternLength: 1,
  musicVolume: 0.5,
  musicTrack: 'fun',
  isFullscreen: false
};

const DEFAULT_STATS: GameStats = {
  highScore: 0,
  totalGamesPlayed: 0,
  averageScore: 0,
  longestStreak: 0,
  lastPlayed: new Date()
};

export function useGameProgress(gameId: string, userId: string) {
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const [stats, setStats] = useState<GameStats>(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProgress = async () => {
      try {
        if (!userId) {
          // Handle guest user case
          setSettings(DEFAULT_SETTINGS);
          setStats({
            highScore: parseInt(localStorage.getItem(`${gameId}-highScore`) || '0'),
            totalGamesPlayed: parseInt(localStorage.getItem(`${gameId}-gamesPlayed`) || '0'),
            averageScore: parseInt(localStorage.getItem(`${gameId}-avgScore`) || '0'),
            longestStreak: parseInt(localStorage.getItem(`${gameId}-streak`) || '0'),
            lastPlayed: new Date()
          });
          setLoading(false);
          return;
        }

        const progress = await getGameProgressForGame(userId, gameId);
        console.log('Loaded progress:', progress);
        if (progress) {
          setSettings(progress.settings || DEFAULT_SETTINGS);
          setStats(progress.stats || DEFAULT_STATS);
        } else {
          console.warn('Game progress is undefined or incomplete');
          setSettings(DEFAULT_SETTINGS);
          setStats(DEFAULT_STATS);
        }
      } catch (err) {
        console.error('Failed to load game progress:', err);
        setError('Failed to load game progress');
      } finally {
        setLoading(false);
      }
    };

    loadProgress();
  }, [gameId, userId]);

  const updateProgress = useCallback(async (update: Partial<GameProgress>) => {
    try {
      const newSettings = { ...settings, ...update.settings };
      const newStats = { ...stats, ...update.stats };
      if (!userId) {
        // Handle guest user case
        localStorage.setItem(`${gameId}-highScore`, newStats.highScore.toString());
        localStorage.setItem(`${gameId}-gamesPlayed`, newStats.totalGamesPlayed.toString());
        localStorage.setItem(`${gameId}-avgScore`, newStats.averageScore.toString());
        localStorage.setItem(`${gameId}-streak`, newStats.longestStreak.toString());
      } else {
        const updatedProgress: GameProgress = {
          userId,
          gameId,
          lastUpdated: new Date(),
          settings: newSettings,
          stats: newStats
        };
        await saveGameProgressForGame(userId, gameId, updatedProgress);
      }
      setSettings(newSettings);
      setStats(newStats);
    } catch (err) {
      setError('Failed to update game progress');
    }
  }, [gameId, userId, settings, stats]);

  return { settings, stats, loading, error, updateProgress };
}